import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  HStack,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { RenderFile } from 'components/RenderFile';
import { dropzone } from 'helpers/makeFormFields';
import { UploadedFileInfo } from 'hooks/mutations/useFileUploadMutation';
import { ReactNode, useEffect, useMemo, useRef } from 'react';
import { Control, FieldValues, useFormContext, UseFormRegister, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { type ChecksRecord, NOT_READABLE_KEY, VerificationStatus } from '../types';
import CheckList from './CheckList';

type DocumentFileData = {
  id: number;
  raw?: { encodedFile?: string; type?: string };
};

type DocumentAccordionPanelProps = {
  documentType: 'identification' | 'business_licence' | 'power_of_attorney' | 'handwerkskarte';
  label: string;
  checksData: ChecksRecord | undefined;
  documentFile: DocumentFileData | null | undefined;
  uploadedFile: UploadedFileInfo | null;
  isReplacing: boolean;
  buttonsLocked: boolean;
  isClarificationReplacement: boolean;
  canReplace: boolean;
  canReplaceCompleted: boolean;
  isUploadPending: boolean;
  isSavePending: boolean;
  isDisabled: boolean;
  currentStatus: number | undefined;
  isStep1: boolean;
  statusIcon: ReactNode;
  onOpenReplacement: () => void;
  onDownload: () => void;
  onDiscard: () => void;
  onSave: () => void;
  isSaveDisabled: boolean;
  isDiscardDisabled: boolean;
};

const ACCORDION_ITEM_STYLE = {
  borderWidth: '1px',
  borderRadius: '6px',
  marginBottom: '16px',
  backgroundColor: 'gray.50',
  boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.10), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
  padding: '12px',
};

const DocumentAccordionPanel = ({
  documentType,
  label,
  checksData,
  documentFile,
  uploadedFile,
  isReplacing,
  buttonsLocked,
  isClarificationReplacement,
  canReplace,
  canReplaceCompleted,
  isUploadPending,
  isSavePending,
  isDisabled,
  currentStatus,
  isStep1,
  statusIcon,
  onOpenReplacement,
  onDownload,
  onDiscard,
  onSave,
  isSaveDisabled,
  isDiscardDisabled,
}: DocumentAccordionPanelProps) => {
  const { t } = useTranslation();
  const {
    setValue,
    control,
    register,
    formState: { errors },
  } = useFormContext<FieldValues>();

  // react-hook-form's Control is invariant — widen here for the dropzone helper
  const formControl = control as unknown as Control<FieldValues>;
  const formRegister = register as unknown as UseFormRegister<FieldValues>;

  const allKeys = useMemo(() => (checksData ? Object.keys(checksData) : []), [checksData]);
  const allChecklistValues = useWatch({ control, name: 'checklist' }) as Record<string, Record<string, string>> | undefined;
  const checklistValues = allChecklistValues?.[documentType];
  const valid = allKeys.filter((k) => checklistValues?.[k] === 'valid');
  const err = allKeys.filter((k) => checklistValues?.[k] === 'error');

  const isNotReadable = checklistValues?.[NOT_READABLE_KEY] === 'error';
  const prevIsNotReadable = useRef(isNotReadable);

  useEffect(() => {
    if (isNotReadable && !prevIsNotReadable.current) {
      allKeys
        .filter((k) => k !== NOT_READABLE_KEY)
        .forEach((k) => setValue(`checklist.${documentType}.${k}`, null));
    }
    prevIsNotReadable.current = isNotReadable;
  }, [isNotReadable, allKeys, documentType, setValue]);

  const setAllValid = () => {
    allKeys.forEach((k) => setValue(`checklist.${documentType}.${k}`, 'valid'));
  };

  const setAllErr = () => {
    allKeys.forEach((k) => setValue(`checklist.${documentType}.${k}`, 'error'));
  };

  const clearChecklist = () => {
    allKeys.forEach((k) => setValue(`checklist.${documentType}.${k}`, null));
  };

  const showDropzone = (isReplacing || documentFile === null) && !uploadedFile?.base64?.encodedFile;
  const hasEncodedFile = !!documentFile?.raw?.encodedFile;

  const resolvedEncodedFile = uploadedFile?.base64?.encodedFile || documentFile?.raw?.encodedFile || '';
  const resolvedType = uploadedFile?.base64?.encodedFile ? uploadedFile.base64.type : (documentFile?.raw?.type || '');

  const isClarificationStatus =
    currentStatus === VerificationStatus.CLARIFICATION_STEP_1 || currentStatus === VerificationStatus.CLARIFICATION_STEP_2;

  const isLocked = buttonsLocked || isClarificationStatus;
  const allApproved = valid.length === allKeys.length && allKeys.length > 0;
  const allRejected = err.length === allKeys.length && allKeys.length > 0;

  return (
    <AccordionItem isDisabled={isDisabled} style={ACCORDION_ITEM_STYLE}>
      <AccordionButton>
        <Flex flex="1" alignItems="center">
          <Heading as="h3" size="xxs">
            {label}
          </Heading>
          {statusIcon}
        </Flex>
        <AccordionIcon />
      </AccordionButton>

      <AccordionPanel pb={4}>
        <Flex padding={{ base: 2, lg: 6 }} flex={1}>
          {/* Document Preview / Upload */}
          <Flex minH={400} flex={2}>
            {showDropzone ? (
              <Flex justifyContent="center" alignItems="center" flex={1}>
                {isUploadPending ? (
                  <LoadingSpinner noMargin />
                ) : (
                  dropzone({
                    name: documentType,
                    required: true,
                    control: formControl,
                    register: formRegister,
                    errors,
                    onChange: (fileData: UploadedFileInfo | null) => {
                      if (!fileData) {
                        clearChecklist();
                      } else {
                        setAllValid();
                      }
                    },
                  } as Parameters<typeof dropzone>[0])
                )}
              </Flex>
            ) : (
              <Flex direction="column" justifyContent="space-between" flex={1}>
                {hasEncodedFile ? (
                  <RenderFile header={label} encodedFile={resolvedEncodedFile} type={resolvedType} />
                ) : (
                  <Flex justifyContent="center" alignItems="center">
                    <Text color="gray.400">{t('workshop_verification_request:no_data')}</Text>
                  </Flex>
                )}
                <Stack>
                  {isClarificationReplacement && (
                    <Alert status="warning" mt={6}>
                      <AlertIcon />
                      <Box>
                        <AlertTitle>{t('workshop_verification_request:clarification.warning.title')}</AlertTitle>
                        <AlertDescription>{t('workshop_verification_request:clarification.warning.description')}</AlertDescription>
                      </Box>
                    </Alert>
                  )}
                  <Flex mt={4}>
                    <Button
                      colorScheme="blue"
                      variant="primary"
                      disabled={!isClarificationReplacement && !canReplaceCompleted && !canReplace}
                      onClick={onOpenReplacement}
                      mr={3}
                    >
                      {t('workshop_verification_request:replace')}
                    </Button>
                    {documentFile != null && (
                      <Button colorScheme="blue" variant="primary" onClick={onDownload}>
                        {t('common:download')}
                      </Button>
                    )}
                  </Flex>
                </Stack>
              </Flex>
            )}
          </Flex>

          {/* Checklist */}
          <Flex direction="column" justifyContent="space-between" ml={5} flex={1}>
            <Flex direction="column">
              <Heading as="h3" size="xxs" mb={5}>
                {t('workshop_verification_request:your_checklist')}
              </Heading>

              <HStack spacing={6} mb={4}>
                <Checkbox
                  isDisabled={isLocked || isNotReadable}
                  isChecked={!isLocked && !isNotReadable && allApproved}
                  onChange={() => {
                    if (allApproved) {
                      clearChecklist();
                    } else {
                      setAllValid();
                    }
                  }}
                >
                  {t('common:approve_all')}
                </Checkbox>
                <Checkbox
                  isDisabled={isLocked || isNotReadable}
                  isChecked={!isLocked && !isNotReadable && allRejected}
                  onChange={() => {
                    if (allRejected) {
                      clearChecklist();
                    } else {
                      setAllErr();
                    }
                  }}
                >
                  {t('common:reject_all')}
                </Checkbox>
              </HStack>

              <Tooltip label={t('workshop_verification_request:clarification.tooltip')} isDisabled={!isClarificationStatus}>
                <div>
                  {checksData &&
                    Object.keys(checksData).map((key) => {
                      const checkItem = checksData[key];
                      return (
                        <CheckList
                          key={key}
                          itemKey={key}
                          title={checkItem?.title || ''}
                          question={checkItem?.question || ''}
                          documentType={documentType}
                          buttonsLocked={isLocked}
                          disabled={isNotReadable && key !== NOT_READABLE_KEY}
                          clarificationReplacement={
                            documentType === 'power_of_attorney'
                              ? !isStep1 && isClarificationReplacement
                              : isStep1 && isClarificationReplacement
                          }
                        />
                      );
                    })}
                </div>
              </Tooltip>
            </Flex>

            <Flex justifyContent="flex-end">
              <Button disabled={isDiscardDisabled} variant="outline" mr={2} onClick={onDiscard}>
                {t('workshop_verification_request:button.discard')}
              </Button>
              <Button
                colorScheme="blue"
                variant="primary"
                minW={110}
                disabled={isSaveDisabled}
                onClick={onSave}
                isLoading={isSavePending}
              >
                {t('common:save')}
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </AccordionPanel>
    </AccordionItem>
  );
};

export default DocumentAccordionPanel;
